import { useMemo } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';

import { EndOfPageRecommendationQuery } from '../EndOfPageRecommendationQuery.graphql.ts';
import {
	type EndOfPageRecommendationQuery as EndOfPageRecommendationData,
	type EndOfPageRecommendationQueryVariables,
} from '../__types__/EndOfPageRecommendationQuery.ts';
import { transformAndOrderRecs } from '../transformers.ts';

const EXPERIMENT_KEY = 'confluence_frontend_stop_watching_queries';

export const usePageRecommendationData = (skip: boolean = false) => {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();

	const baseOptions = {
		variables: {
			type: 'page',
			id: contentId ?? '',
			exp: 'end_of_page_v5',
			key: spaceKey ?? '',
			keyString: spaceKey ?? '',
		},
		context: { single: true },
		errorPolicy: 'all' as const,
		skip,
	};

	const { error, loading, data } = expValEquals(EXPERIMENT_KEY, 'isEnabled', true)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryNoFurtherUpdateInterop<
				EndOfPageRecommendationData,
				EndOfPageRecommendationQueryVariables
			>(EndOfPageRecommendationQuery, baseOptions)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryInterop<EndOfPageRecommendationData, EndOfPageRecommendationQueryVariables>(
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				EndOfPageRecommendationQuery,
				baseOptions,
			);

	const pageCardData = useMemo(
		() => transformAndOrderRecs(data?.getRecommendedPages?.recommendedPages),
		[data],
	);

	if (error || skip) {
		return {
			loading: false,
			error,
			data: undefined,
			hasRecs: false,
		};
	}

	return {
		loading,
		error,
		data: pageCardData,
		hasRecs: pageCardData && pageCardData.length > 2,
	};
};
